import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import CompensationsOut from "@/components/CompensationsOut";
import { AwaitingPaymentsPanel } from "@/pages/AccountingAwaitingPayments";
import UserAvatar from "@/components/UserAvatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return "—"; }
};
const fmtMonthLabel = (m) => {
  if (!m) return "";
  const [y, mm] = m.split("-");
  const label = new Date(y, Number(mm) - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

export default function Accounting() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/dashboard/accounting").then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  // Gate: la contabilità è visibile solo a admin puro e accounting_operator.
  // I supervisor (anche se privilegiati) NON possono accedere qui.
  if (!user || (user.role !== "admin" && user.role !== "accounting_operator")) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center p-6 border rounded-lg">
        <h2 className="text-lg font-semibold mb-2">Accesso non consentito</h2>
        <p className="text-sm text-muted-foreground">Questa sezione è riservata al team Contabilità e Admin.</p>
      </div>
    );
  }

  if (!data) return <div>Caricamento...</div>;

  return (
    <div>
      <div className="mb-6">
        <div className="label-eyebrow">Contabilità</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Contabilità</h1>
        <p className="text-muted-foreground mt-2">Gestisci incassi dai professionisti e compensi degli operatori</p>
      </div>

      <Tabs defaultValue="professionals" className="w-full">
        <TabsList className="mb-6" data-testid="accounting-tabs">
          <TabsTrigger value="professionals" data-testid="tab-accounting-professionals">
            Contabilità Professionisti
          </TabsTrigger>
          <TabsTrigger value="operators" data-testid="tab-accounting-operators">
            Compensi Operatori
          </TabsTrigger>
          <TabsTrigger value="awaiting" data-testid="tab-accounting-awaiting">
            Pagamenti in attesa
          </TabsTrigger>
        </TabsList>

        <TabsContent value="professionals" className="space-y-8">
          {/* Iter175 — Rimossa la card "Totale Da Incassare" (duplicata a 0€ per
              alcuni utenti). L'unico riepilogo top è "Totale Incassato". */}
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <div className="kpi-card">
              <div className="label-eyebrow">Totale Incassato</div>
              <div className="font-display text-4xl font-bold mt-2 text-green-700" data-testid="tot-paid">{fmt(data.total_paid)}</div>
            </div>
          </div>

          <div className="kpi-card">
            <h3 className="font-display text-xl font-semibold mb-4">Professionisti</h3>
            <table className="w-full text-sm mobile-cards">
              <thead><tr className="text-left border-b">
                <th className="py-2">Nome</th><th>Email</th><th className="text-right">Pratiche</th>
                <th className="text-right">Plafond usato</th><th className="text-right">Incassato</th>
                <th className="text-right">Da incassare</th><th></th>
              </tr></thead>
              <tbody>
                {data.professionals.map(p => (
                  <tr key={p.professional_id} className={`border-b cursor-pointer ${p.arrears_total > 0 ? "bg-amber-50 hover:bg-amber-100/70" : "hover:bg-[color:hsl(var(--surface-alt))]"}`}
                    onClick={() => setExpanded(expanded === p.professional_id ? null : p.professional_id)}
                    data-testid={`acc-prof-${p.professional_id}`}>
                    <td data-label="Nome" className="py-3 font-medium">
                      {p.professional_company_name || p.professional_name}
                      {p.arrears_total > 0 && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 border border-amber-300 px-2 py-0.5 text-[10px] font-semibold text-amber-800 whitespace-nowrap" data-testid={`acc-prof-arrears-${p.professional_id}`}>
                          Arretrati {fmt(p.arrears_total)}
                        </span>
                      )}
                    </td>
                    <td data-label="Email">{p.email}</td>
                    <td data-label="Pratiche" className="text-right font-mono">{p.count}</td>
                    <td data-label="Plafond usato" className="text-right font-mono">{fmt(p.plafond_used)}</td>
                    <td data-label="Incassato" className="text-right font-mono text-green-700">{fmt(p.paid_total)}</td>
                    <td data-label="Da incassare" className="text-right font-mono text-purple-600">{fmt(p.unpaid_total)}</td>
                    <td className="text-right text-xs mobile-actions">{expanded === p.professional_id ? "▲" : "▼"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {expanded && <ProfessionalReport pid={expanded} onPaid={load} />}
        </TabsContent>

        <TabsContent value="operators">
          <CompensationsOut />
        </TabsContent>

        <TabsContent value="awaiting">
          <AwaitingPaymentsPanel showHeader={false} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ProfessionalReport({ pid, onPaid }) {
  const [rep, setRep] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'paid'|'unpaid', pr }

  const load = async () => {
    const r = await api.get(`/accounting/professional/${pid}`);
    setRep(r.data);
    if (r.data.months?.length) {
      setSelectedMonth(prev => prev || r.data.months[0].month);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  const currentMonth = useMemo(() => {
    if (!rep) return null;
    return rep.months.find(m => m.month === selectedMonth) || null;
  }, [rep, selectedMonth]);

  const doMarkPaid = async (prid) => {
    const res = await api.post(`/practices/${prid}/mark-paid`);
    toast.success(`Pagamento registrato il ${fmtDate(res.data?.paid_at)}`);
    setConfirmAction(null);
    await load();
    onPaid && onPaid();
  };
  const doMarkUnpaid = async (prid) => {
    await api.post(`/practices/${prid}/mark-unpaid`);
    toast.success("Riportato a Non pagato");
    setConfirmAction(null);
    await load();
    onPaid && onPaid();
  };

  if (!rep) return <div className="kpi-card mt-4 text-sm text-muted-foreground">Caricamento…</div>;
  const p = rep.professional;

  return (
    <div className="kpi-card mt-4" data-testid="prof-report">
      {/* Anagrafica */}
      <div className="mb-6">
        <div className="flex items-start gap-4 flex-wrap">
          <UserAvatar user={p.company_name ? { ...p, first_name: "", last_name: "" } : p} size={64} testId="prof-avatar" />
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow">Professionista · {p.role}</div>
            <h3 className="font-display text-2xl font-bold" data-testid="prof-report-name">
              {p.company_name || `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.email}
            </h3>
            <div className="text-xs text-muted-foreground mt-1">
              {p.company_name && <div><strong>Ragione sociale:</strong> {p.company_name}</div>}
              {p.company_toponimo && <div><strong>Sede:</strong> {p.company_toponimo} {p.company_address} {p.company_civico}, {p.company_city}</div>}
              {p.company_vat && <div><strong>P.IVA:</strong> {p.company_vat}</div>}
            </div>
          </div>
          <div className="text-xs text-right space-y-0.5">
            <div>📧 {p.company_email || p.email}</div>
            {p.company_pec && <div>PEC: {p.company_pec}</div>}
            {p.company_phone && <div>Az: {p.company_phone}</div>}
            {(p.owner_phone || p.phone) && <div>Ref: {p.owner_phone || p.phone}</div>}
          </div>
        </div>
        {rep.arrears_total > 0 && (
          <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="acc-arrears-banner">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-sm font-semibold text-amber-900">Arretrati dei mesi precedenti</div>
              <div className="font-display font-bold text-amber-900" data-testid="acc-arrears-total">{fmt(rep.arrears_total)}</div>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {rep.arrears_months.map((m) => (
                <button
                  key={m.month}
                  type="button"
                  onClick={() => {
                    setSelectedMonth(m.month);
                    document.getElementById(`acc-month-detail-${pid}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100 transition-colors"
                  data-testid={`acc-arrears-month-${m.month}`}
                >
                  {m.label} · {fmt(m.net_due)}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Iter175 — Card riepilogative riordinate:
            (1) Pratiche Totali (2) Incassato (3) Plafond Mese
            (4) Credito Accumulato (unificato) (5) Netto Dovuto ad Housetag.
            Formula (Iter196): Netto Dovuto = max(0, Plafond Mese − Credito Accumulato). */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4 text-center">
          <div className="p-2 rounded bg-purple-50 border border-purple-200" data-testid="card-pratiche-totali">
            <div className="text-[10px] uppercase text-purple-700">Pratiche Totali</div>
            <div className="font-display font-bold text-purple-800">{rep.count}</div>
          </div>
          <div className="p-2 rounded bg-green-50 border border-green-200" data-testid="card-incassato">
            <div className="text-[10px] uppercase text-green-700">Incassato</div>
            <div className="font-display font-bold text-green-800">{fmt(rep.total_paid)}</div>
          </div>
          <div className="p-2 rounded bg-slate-50 border border-slate-200" data-testid="card-plafond-mese">
            <div className="text-[10px] uppercase">Plafond Mese</div>
            <div className="font-display font-bold">{fmt(p.plafond_used_current_month)}</div>
          </div>
          <div
            className="p-2 rounded bg-emerald-50 border border-emerald-200"
            title="Credito accumulato dal PRO (margini/ricarichi maturati su pratiche shared)"
            data-testid="card-credito-accumulato"
          >
            <div className="text-[10px] uppercase text-emerald-700">Credito Accumulato</div>
            <div className={`font-display font-bold ${Number(p.accumulated_credit) < 0 ? "text-red-600" : "text-emerald-800"}`}>
              {fmt(p.accumulated_credit)}
            </div>
          </div>
          <div
            className="p-2 rounded bg-amber-50 border border-amber-200"
            title="Netto Dovuto ad Housetag = max(0, Plafond Mese − Credito Accumulato)"
            data-testid="card-netto-dovuto"
          >
            <div className="text-[10px] uppercase text-amber-700">Netto Dovuto ad Housetag</div>
            <div className="font-display font-bold text-amber-800" data-testid="tot-net-due">
              {fmt(rep.total_net_due_housetag)}
            </div>
          </div>
        </div>
        {rep.fascia && <div className="mt-2 text-[11px] text-muted-foreground">Fascia: <strong>{rep.fascia.name}</strong> · Plafond: {rep.plafond?.name || "—"}</div>}
      </div>

      {/* Selettore mese */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h4 id={`acc-month-detail-${pid}`} className="font-display text-lg font-semibold scroll-mt-24">Dettaglio pratiche</h4>
        {rep.months.length > 0 && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">Mese:</label>
            <select
              className="border rounded px-3 py-1.5 text-sm bg-white"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              data-testid="month-select"
            >
              {rep.months.map(m => (
                <option key={m.month} value={m.month}>
                  {fmtMonthLabel(m.month)} · {m.practices.length} pratiche
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {rep.months.length === 0 || !currentMonth ? (
        <div className="text-sm text-muted-foreground py-6 text-center">Nessuna pratica</div>
      ) : (
        <>
          {/* Iter175 — Riepilogo mensile riordinato:
              Pratiche → Incassato → Plafond mese → Credito → Netto Dovuto (ultimo). */}
          <div className="flex items-center gap-4 text-xs mb-3 px-1 flex-wrap">
            <span className="text-muted-foreground" data-testid="row-pratiche">{currentMonth.practices.length} pratiche</span>
            <span className="text-green-700 font-mono" data-testid="row-incassato">Incassato {fmt(currentMonth.paid)}</span>
            <span className="text-slate-700 font-mono" data-testid="row-plafond">
              Plafond {fmt(currentMonth.plafond_used ?? (currentMonth.net_due + currentMonth.credit_earned))}
            </span>
            {currentMonth.credit_earned !== 0 && (
              <span
                className={`font-mono ${currentMonth.credit_earned < 0 ? "text-red-600" : "text-emerald-700"}`}
                data-testid="row-credito"
              >
                Credito {fmt(currentMonth.credit_earned)}
              </span>
            )}
            <span
              className="text-amber-700 font-mono"
              title="Netto Dovuto ad Housetag = max(0, Plafond mese − Credito)"
              data-testid="row-netto-dovuto"
            >
              Netto Dovuto ad Housetag {fmt(currentMonth.net_due)}
            </span>
          </div>
          <div className="border rounded overflow-x-auto">
            <table className="w-full text-sm mobile-cards">
              <thead className="bg-[color:hsl(var(--surface-alt))] text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Servizio</th>
                  <th className="text-left px-3 py-2">Proprietario</th>
                  <th className="text-left px-3 py-2">Indirizzo</th>
                  <th className="text-left px-3 py-2">Metodo</th>
                  <th className="text-right px-3 py-2">Importo</th>
                  <th className="text-left px-3 py-2">Pagata il</th>
                  <th className="text-right px-3 py-2">Stato</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {currentMonth.practices.map(pr => (
                  <tr key={pr._id} data-testid={`row-${pr._id}`}>
                    <td data-label="Servizio" className="px-3 py-2 font-medium">{pr.service_name || "—"}</td>
                    <td data-label="Proprietario" className="px-3 py-2">{pr.owner_name || "—"}</td>
                    <td data-label="Indirizzo" className="px-3 py-2 text-xs text-muted-foreground">{pr.property_address || "—"}</td>
                    <td data-label="Metodo" className="px-3 py-2 text-xs">{pr.payment_method || "—"}</td>
                    <td data-label="Importo" className="px-3 py-2 text-right font-mono">{fmt(pr.total_price)}</td>
                    <td data-label="Pagata il" className="px-3 py-2 text-xs">{pr.payment_status === "paid" ? fmtDate(pr.paid_at) : "—"}</td>
                    <td data-label="Stato" className="px-3 py-2 text-right mobile-actions">
                      {pr.payment_status === "paid" ? (
                        <div className="flex items-center justify-end gap-2">
                          <span className="role-badge bg-green-100 text-green-800">Pagata</span>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7"
                            onClick={() => setConfirmAction({ type: "unpaid", pr })}
                            data-testid={`revert-${pr._id}`}
                          >Non pagato</Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => setConfirmAction({ type: "paid", pr })}
                          className="bg-[#166534]"
                          data-testid={`mark-paid-${pr._id}`}
                        >Segna pagata</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Doppia conferma */}
      <AlertDialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === "paid" ? "Confermi il pagamento?" : "Riportare a Non pagato?"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <div>
                  {confirmAction?.type === "paid" ? (
                    <>Stai per registrare l'incasso di questa pratica. La data odierna verrà salvata come data di pagamento.</>
                  ) : (
                    <>Stai per annullare il pagamento. La data verrà rimossa e la pratica tornerà nello stato "Non pagata".</>
                  )}
                </div>
                {confirmAction?.pr && (
                  <div className="mt-3 p-3 rounded bg-slate-50 border text-xs space-y-0.5">
                    <div><strong>Servizio:</strong> {confirmAction.pr.service_name || "—"}</div>
                    <div><strong>Proprietario:</strong> {confirmAction.pr.owner_name || "—"}</div>
                    <div><strong>Indirizzo:</strong> {confirmAction.pr.property_address || "—"}</div>
                    <div><strong>Importo:</strong> {fmt(confirmAction.pr.total_price)}</div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-ok"
              onClick={() => {
                if (!confirmAction) return;
                if (confirmAction.type === "paid") doMarkPaid(confirmAction.pr._id);
                else doMarkUnpaid(confirmAction.pr._id);
              }}
              className={confirmAction?.type === "paid" ? "bg-[#166534] hover:bg-[#14532d]" : ""}
            >
              {confirmAction?.type === "paid" ? "Sì, conferma pagamento" : "Sì, riporta a Non pagato"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
